import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage, MessageValue } from 'react-intl';
import _memoize from 'lodash/memoize';
import { hooks } from '@lifesize/clients.sdk';
import { usersActions } from '@lifesize/nucleus';
import { colors } from '@lifesize/ux-assets';
import { ReactComponent as InfoIcon } from '@lifesize/ux-assets/vectors/svgIcons/information-outline.svg';
import { ReactComponent as HandIcon } from '@lifesize/ux-assets/vectors/svgIcons/hand.svg';
import { Fab, makeStyles, Theme, Tooltip } from '@material-ui/core';
import { setPillNotificationWithTimeout } from 'actions/Notification';
import { FLAG_RAISE_HAND } from 'constants/FeatureFlags';
import { tooltipNotificationTypes } from 'constants/Notification';
import useAppNotification, { AppNotification, NotificationType } from 'hooks/useAppNotification';
import useFeatureFlag from 'hooks/useFeatureFlag';

export interface RaiseHandNotification {
  defaultMessage?: string;
  id?: string;
  messageType: string;
  values?: { [key: string]: MessageValue | JSX.Element };
}

/** Style selectors that can be dynamically modified */
interface StyleProps {
  backgroundColor: string;
  borderColor: string;
  opacity: number;
}

const getStyleOverride = _memoize(
  (isRaised: boolean): StyleProps => {
    return {
      backgroundColor: isRaised ? colors.agileGreen : 'transparent',
      borderColor: isRaised ? colors.agileGreen : colors.white,
      opacity: isRaised ? 1.0 : 0.6
    };
  }
);

const RaiseHandButton = () => {
  const dispatch = useDispatch();
  const canRaiseHand = hooks.useCanIssueHandCommand();
  const handIsProcessing = hooks.useIsProcessingHandCommand();
  const handIsRaised = hooks.useIsCurrentUserHandRaised();
  const isRaised = handIsProcessing ? !handIsRaised : handIsRaised;
  const isConnected = hooks.useCallConnected();
  const isDisabled = !isConnected || !canRaiseHand;
  const showRaiseHandsButton = useFeatureFlag(FLAG_RAISE_HAND);
  const [isTooltipOpen, setTooltipOpen] = useState(false);
  const [label, setLabel] = useState<NonNullable<React.ReactNode>>(<></>);
  const notification: AppNotification | undefined = useAppNotification(
    NotificationType.TOOLTIP_IN_CALL_ACTION_RAISE_HAND
  );
  const classes = useStyles({ ...getStyleOverride(isRaised) });

  const handleClose = useCallback(() => {
    setTooltipOpen(false);
  }, []);

  const handleOpen = useCallback(() => {
    setTooltipOpen(true);
  }, []);

  const handleButtonClick = useCallback(() => {
    if (!!isRaised) {
      dispatch(usersActions.lowerOwnHand());
    } else {
      dispatch(usersActions.raiseHand());
    }
  }, [dispatch, isRaised]);

  const getSnackbarLabel = useCallback(() => {
    if (!!notification) {
      const hasNotificationError = notification?.messageType === tooltipNotificationTypes.error;
      if (hasNotificationError)
        return (
          <div className={`${classes.tooltipLabelWithIcon} ${classes.error}`}>
            <InfoIcon data-testid="infoIcon" />
            <div className={classes.message}>
              <FormattedMessage
                id={notification?.id || ''}
                defaultMessage={notification.defaultMessage}
                values={notification.values}
              />
            </div>
          </div>
        );
      return (
        <FormattedMessage
          id={notification?.id || ''}
          defaultMessage={notification.defaultMessage}
          values={notification.values}
        />
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notification]);

  useEffect(() => {
    if (!!notification) {
      setTooltipOpen(false);
      dispatch(setPillNotificationWithTimeout(getSnackbarLabel()));
    }
  }, [notification, getSnackbarLabel, setTooltipOpen, dispatch]);

  const getTooltipLabel = useCallback((isRaised) => {
    if (isRaised) {
      return (
        <FormattedMessage id="components.incall.controls.raiseHand.tooltip.lowerHand" defaultMessage="Lower hand" />
      );
    }
    return <FormattedMessage id="components.incall.controls.raiseHand.tooltip.raiseHand" defaultMessage="Raise hand" />;
  }, []);

  /** This prevents the text from changing while the tooltip is open */
  useEffect(() => {
    if (!handIsProcessing && !isTooltipOpen) {
      setLabel(getTooltipLabel(isRaised));
    }
  }, [isRaised, handIsProcessing, getTooltipLabel, isTooltipOpen]);

  if (!showRaiseHandsButton) return <></>;

  return (
    <Tooltip arrow={true} onClose={handleClose} onOpen={handleOpen} open={isTooltipOpen} placement="top" title={label}>
      <span data-testid="raise-hand-btn">
        <Fab
          classes={{ root: classes.root, disabled: classes.disabled }}
          disabled={isDisabled}
          disableFocusRipple={true}
          onClick={handleButtonClick}
          size="medium"
        >
          <HandIcon data-testid="hand-icon" />
        </Fab>
      </span>
    </Tooltip>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: (btnStyle: StyleProps) => btnStyle.backgroundColor,
    border: (btnStyle: StyleProps) => `2px solid ${btnStyle.borderColor}`,
    boxShadow: 'none',
    color: colors.white,
    margin: 0,
    opacity: (btnStyle: StyleProps) => btnStyle.opacity,
    '&:hover': {
      backgroundColor: (btnStyle: StyleProps) => btnStyle.backgroundColor,
      borderColor: (btnStyle: StyleProps) => btnStyle.borderColor,
      color: colors.white,
      opacity: 1
    },
    '& svg': {
      height: 40,
      width: 'auto'
    }
  },
  disabled: {
    color: `${colors.sentinel} !important`,
    borderColor: `${colors.sentinel} !important`,
    backgroundColor: `${colors.squidInk} !important`,
    opacity: `1 !important`
  },
  tooltipLabelWithIcon: {
    alignItems: 'center',
    display: 'flex',
    svg: {
      flex: '0 0 auto',
      height: 36
    }
  },
  message: {
    flex: '1 0 auto',
    marginLeft: '10px'
  },
  error: {
    color: colors.alertRedDark,
    svg: {
      transform: 'rotate(180deg)'
    }
  }
}));

export default memo(RaiseHandButton);
